import S from 's-js';
import { IAjax } from 'lib/Ajax';
import UOM from 'lib/UOM';
import { MeasurementInFeetAndInches } from 'lib/MeasurementInFeetAndInches';
import { tzmoment } from 'lib/tzmoment';

import { Entrypoints } from 'core/app/entrypoints';

import { VesselSide, VesselSideDisplayName } from 'server/enums/BerthReservation';

import { BerthReservation } from 'data/models/BerthReservation';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { NullableMeasurement } from 'data/models/Measurement';
import { PortCallReport as ServerPortCallReport, PortCallReportOptions } from 'data/models/PortCallReport';
import { PortCall } from 'data/models/PortCall';
import { Vessel } from 'data/models/Vessel';

import { PortCallProfileCache } from './cache';

export interface PortCallReportParams {
    pc : PortCall;
    hideActionsMenu : () => void;
}

export type PortCallReport = ReturnType<typeof PortCallReport>;
export function PortCallReport(
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : PortCallProfileCache,
    { pc, hideActionsMenu } : PortCallReportParams
) {
    const
        url = () => entrypoints.ViewPortCallReport({ PortCallId: pc.Id() }),
        client = ajax.Url<{}, PortCallReportOptions>(url),
        displayedPortCall = S.value<ServerPortCallReport | null>(null),
        showReportUnavailableMsg = S.value(false),
        loading = S.value(false);

    // For doing dev on UI/styling. I found this useful since otherwise this component's UI is
    // only rendered while the print dialog is open. To use:
    //
    // 1) Set a breakpoint on the statement after this one.
    // 2) Open the PortCall profile.
    // 3) Your breakpoint will be hit. When that happens, run the following in your browser's dev
    //    console:
    //
    //    attachDevTools()
    //
    // 4) Continue from your breakpoint. Now, as long as that PortCall is there, you can run the
    //    following commands in your browser's dev console:
    //
    //    PortCallReportDevTools.ShowPrintView()
    //    PortCallReportDevTools.HidePrintView()
    //
    // 5) You may also find it helpful to emulate @media print. Instructions for how to do this
    //    in Chrome at https://stackoverflow.com/a/29962072. Summary:
    //       - Open dev tools
    //       - Open hamburger menu in top right corner (tooltip reads "Customize and control DevTools")
    //       - Go to More tools > Rendering
    //       - Find "Emulate CSS media" option (near bottom) and select "print"
    const attachDevTools = () => {
        (window as any).PortCallReportDevTools = {
            ShowPrintView: async () => displayedPortCall(await LoadPortCallReport()),
            HidePrintView: () => displayedPortCall(null)
        };
    };

    return {
        DisplayedPortCall: S(() =>
            displayedPortCall() &&
            PortCallReportViewModel(displayedPortCall()!.CurrentPC()!)
        ),
        ShowReportUnavailableMsg: showReportUnavailableMsg,
        Loading: loading,
        LinkAvailable,
        PrintOrder
    };

    function LinkAvailable() {
        // 08/23/2021 -- Could become available once we have an Actual Departure.
        // if (pc && pc.ActiveReservations) console.log(pc.HasDeparted);
        return pc.Id() > 0;
    }

    async function PrintOrder() {
        if (loading()) return;
        loading(true);
        try {
            displayedPortCall(await LoadPortCallReport());
            try {
                window.print();
            } finally {
                displayedPortCall(null);
            }
        } finally {
            S.freeze(() => {
                loading(false);
                hideActionsMenu();
            });
        }
    }

    // NOTE: Tim originally tried using a CachedModelLoader here instead of this function.
    // Using this function instead so we can implement custom error handling logic based on
    // the response status. CachedModelLoader doesn't currently expose the error response
    // (well, unless you check ajax.LastResponse, but that makes it hard to know you've got the
    // right response).
    async function LoadPortCallReport() {
        const req = client.Get();
        try {
            const orderData = await req;
            return cache.PortCallReports.update1(req.Date, orderData);
        } catch (ex) {
            const resp = req.Response();
            // 404 signals that we couldn't find the original order data for this PortCall.
            // That most likely means the PortCall predates our version tables. Show an
            // appropriate error message.
            if (resp && resp.Status === 404) {
                showReportUnavailableMsg(true);
            }
            throw ex;
        }
    }
}

export type PortCallReportViewModel = ReturnType<typeof PortCallReportViewModel>;
function PortCallReportViewModel(
    pc : PortCall
) {
    const
        lengthUOM = () => pc.Port() && pc.Port()!.PierLengthUOM() || UOM.Foot,
        itinerary = pc.Itinerary.sort(ItineraryPoint.EstimatedOrder),
        itineraryPoints = S(() => {
            const _itinerary = itinerary();
            return _itinerary.slice(0, -1).map((_, index) => {
                const ip1 = _itinerary[index];
                const ip2 = _itinerary[index + 1];
                return ItineraryViewModel(ip1, ip2);
            });
        }),
        port = () => pc.Port() && pc.Port()!.Name(),

        // 1d. Times (for each reservation)
        // 1di. First Arrival (aka Pilot Boarding) (Est. vs Act. [vs Eff.])
        // 1dii. InPort Arrival (aka First Line) (Est. vs Act. [vs Eff.])
        // 1diii. Departure (aka Last Line) (Est. vs Act. [vs Eff.])


        // 6. PJOs present (don't think we can get more than that?)
        pjos = pc.JobOrders().length,
        // pjos = true,

        // 7. PortCall Id
        pcId = () => pc.Id();

    return {
        Model: pc,
        LengthDisplay: lengthDisplay(pc.VesselLength, lengthUOM),
        Vessel: S(() => pc.Vessel() && PortCallVesselViewModel(pc.Vessel()!, lengthUOM)),
        itinerary: itinerary,
        ItineraryPoints: itineraryPoints,
        Port: port,
        PJOs: pjos,
        PCId: pcId
    };
}

export type PortCallVesselViewModel = ReturnType<typeof PortCallVesselViewModel>;
function PortCallVesselViewModel(
    vessel : Vessel,
    lengthUOM : () => string
) {
    return {
        Model: vessel,
        BeamDisplay: lengthDisplay(vessel.Beam, lengthUOM),
        GrossTonnageDisplay: () => vessel.GrossTonnage.toString({ units: false })
    };
}

export type ItineraryViewModel = ReturnType<typeof ItineraryViewModel>;
function ItineraryViewModel(
    ip1 : ItineraryPoint,
    ip2 : ItineraryPoint,
) {
    const
        key = ip1.Id(),
        portArrivalIP = ip1.IsPortArrival() ? ip1 : null,
        portDepartureIP = ip1.IsPortDeparture() ? ip1 : null,
        berthReservationIP = ip1.IsPortArrival() || ip1.IsPortDeparture() ? null : ip1,
        isSameDayReservation = berthReservationIP && (tzmoment(berthReservationIP.Arrival.Estimated()).format('MMM D, YYYY') === tzmoment(berthReservationIP.Departure.Estimated()).format('MMM D, YYYY')) ? true : false,
        isSameDayTransit = ip1 && (tzmoment(ip1.Departure.Estimated()).format('MMM D, YYYY') === tzmoment(ip2.Arrival.Estimated()).format('MMM D, YYYY')),
        referenceReservation = () => ip1.BerthReservation();

    return {
        Key: key,
        PortArrival: portArrivalIP,
        PortDeparture: portDepartureIP,
        BerthRes: berthReservationIP,
        FirstLineEst: ip1.Arrival.Estimated,
        FirstLineAct: ip1.Arrival.Actual,
        LastLineEst: ip1.Departure.Estimated,
        LastLineAct: ip1.Departure.Actual,
        isSameDayReservation: isSameDayReservation,
        IsSameDayTransit: isSameDayTransit,
        Reservation: S(() => referenceReservation() && BerthReservationViewModel(referenceReservation()!))
    };
}

export type BerthReservationViewModel = ReturnType<typeof BerthReservationViewModel>;
function BerthReservationViewModel(
    reservation : BerthReservation,
) {
    return {
        // Rounding behavior intended to match the FootmarkAssignments widget
        FootmarkNum: () => Math.round(reservation.Footmark.Num()),
        EndmarkNum: () => Math.round(reservation.EndMark.Num()),
        SideToText: () =>
            reservation.SideTo() === VesselSide.Starboard ? 'Starboard' :
            reservation.SideTo() === VesselSide.Port ? 'Port' :
            VesselSideDisplayName[reservation.SideTo()],
        Cargo: () => reservation.CargoActivities(),
        Cruise: () => reservation.CruiseActivities()
    };
}

function lengthDisplay(m : NullableMeasurement, uom : () => string) {
    return () => uom() === UOM.Foot ?
        new MeasurementInFeetAndInches(m, 0)
            .toString({ unitsStyle: 'abbrev', padInchesLeft: false }) :
        m.convert(UOM.Meter).roundToNPlaces(0).toString();
}



// WEBPACK FOOTER //
// ./apps/portcall/portcallprofile/portcallreport.ts